import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { TaskCard } from "./TaskCard"
import { Trash2 } from "lucide-react"
import type { ExtendedTask } from "@/types/task"
import { useAuthStore } from "@/store/authStore"
import { doc, deleteDoc } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { toast } from "@/hooks/use-toast"
import { useState, useMemo } from "react"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Timestamp } from "firebase/firestore"

import { BlockCard, taskListTheme, TaskListHeader } from "./ui/TaskListUI"

interface TaskListProps {
  blocks: ExtendedTask[]
  regularTasks: ExtendedTask[]
  onBlockSelect: (block: ExtendedTask) => void
  onTaskSelect: (task: ExtendedTask) => void
  deadlineFilter: "all" | "overdue" | "today" | "tomorrow" | "thisWeek" | "nextWeek" | "noDeadline"
  onDeadlineFilterChange?: (filter: "all" | "overdue" | "today" | "tomorrow" | "thisWeek" | "nextWeek" | "noDeadline") => void
}

export function TaskList({ 
  blocks, 
  regularTasks, 
  onBlockSelect, 
  onTaskSelect, 
  deadlineFilter,
  onDeadlineFilterChange 
}: TaskListProps) {

  const { user } = useAuthStore()
  const userId = user ? Number(user.id) : 0

  const showMyTasksToggle = [1, 2, 3, 4].includes(userId)
  const [filterType, setFilterType] = useState<"my" | "assigned" | "all" | "blocks">("my")

  const [sortBy, setSortBy] = useState<
    "urgency" | "deadline-asc" | "deadline-desc" | "creator-asc" | "creator-desc"
  >("urgency")

  const displayedRegularTasks = useMemo(() => {
    let filtered = [...regularTasks]

    if (showMyTasksToggle) {
      if (filterType === "my") {
        filtered = filtered.filter((task) => 
          task.assigneeId === userId ||
          (task.coAssigneeIds?.includes(userId) ?? false) ||
          (task.observerIds?.includes(userId) ?? false)
        )
      } else if (filterType === "assigned") {
        filtered = filtered.filter((task) => {
          const creatorId = task.creatorId ?? Number(task.createdById) ?? 0
          return creatorId === userId
        })
      } else if (filterType === "blocks") {
        filtered = filtered.filter((task) => 
          !!task.parentId &&
          (
            task.assigneeId === userId ||
            (task.coAssigneeIds?.includes(userId) ?? false) ||
            (task.observerIds?.includes(userId) ?? false)
          )
        )
      }
    }

    if (deadlineFilter !== "all") {
      filtered = filtered.filter((task) => {
        if (!task.deadline) return deadlineFilter === "noDeadline"

        const deadlineDate = new Date(task.deadline)
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

        const diffTime = deadlineDate.getTime() - today.getTime()
        const diffDays = Math.ceil(diffTime / (1000 * 3600 * 24))

        if (deadlineFilter === "overdue") return diffDays < 0
        if (deadlineFilter === "today") return diffDays === 0
        if (deadlineFilter === "tomorrow") return diffDays === 1
        if (deadlineFilter === "thisWeek") return diffDays > 1 && diffDays <= 7
        if (deadlineFilter === "nextWeek") return diffDays > 7 && diffDays <= 14
        if (deadlineFilter === "noDeadline") return false

        return false
      })
    }

    return filtered.sort((a, b) => {
      if (sortBy === "urgency") {
        if (a.urgency === "urgent" && b.urgency !== "urgent") return -1
        if (a.urgency !== "urgent" && b.urgency === "urgent") return 1
      }

      if (sortBy === "deadline-asc" || sortBy === "deadline-desc") {
        const dateA = a.deadline ? new Date(a.deadline).getTime() : Infinity
        const dateB = b.deadline ? new Date(b.deadline).getTime() : Infinity
        return sortBy === "deadline-asc" ? dateA - dateB : dateB - dateA
      }

      if (sortBy === "creator-asc" || sortBy === "creator-desc") {
        const creatorA = (a.createdBy || a.creatorId?.toString() || "").toLowerCase()
        const creatorB = (b.createdBy || b.creatorId?.toString() || "").toLowerCase()
        return sortBy === "creator-asc"
          ? creatorA.localeCompare(creatorB)
          : creatorB.localeCompare(creatorA)
      }

      return getTimestamp(b.createdAt) - getTimestamp(a.createdAt)
    })
  }, [regularTasks, filterType, deadlineFilter, userId, showMyTasksToggle, sortBy])

  const handleDeleteBlock = async (blockId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm("Удалить блок задач навсегда? Это удалит все подзадачи внутри!")) return
    try {
      await deleteDoc(doc(db, "tasks", blockId))
      toast({
        title: "Блок удалён",
        description: "Успешно удалено вместе со всеми подзадачами",
      })
    } catch (error) {
      console.error("Ошибка удаления блока:", error)
      toast({
        title: "Ошибка",
        description: "Не удалось удалить блок",
        variant: "destructive",
      })
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
      <div className="lg:col-span-1 space-y-4">
        <h2 className={taskListTheme.pageTitle}>Блоки задач</h2>
        {blocks.length === 0 ? (
          <p className={taskListTheme.emptyState}>Блоков задач нет</p>
        ) : (
          blocks.map((blockTask) => {
            const isNewTask = blockTask.unreadBy?.includes(userId)
            const unreadMessagesInBlock = (blockTask.subTasks || []).reduce(
              (sum, sub) => sum + (sub.unreadMessagesBy?.[userId] || 0),
              0
            )

            return (
              <BlockCard
                key={blockTask.id}
                onClick={() => onBlockSelect(blockTask)}
              >
                <h3 className="text-xl font-semibold text-white text-center">{blockTask.title}</h3>

                {isNewTask && (
                  <Badge className={taskListTheme.badgeNew}>
                    Новая задача
                  </Badge>
                )}

                {unreadMessagesInBlock > 0 && (
                  <Badge className={taskListTheme.badgeUnread}>
                    {unreadMessagesInBlock > 99 ? "99+" : unreadMessagesInBlock}
                  </Badge>
                )}

                {userId === 4 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => handleDeleteBlock(blockTask.id!, e)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-full h-10 w-10 p-0 transition-colors duration-200"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                )}
              </BlockCard>
            )
          })
        )}
      </div>

      <div className="lg:col-span-4">
        <TaskListHeader>
          {showMyTasksToggle && (
            <Tabs value={filterType} className="w-auto">
              <TabsList className={taskListTheme.tabsList}>
                <TabsTrigger
                  value="my"
                  className={taskListTheme.tabTrigger}
                  onClick={() => setFilterType("my")}
                >
                  Мои задачи
                </TabsTrigger>
                <TabsTrigger
                  value="assigned"
                  className={taskListTheme.tabTrigger}
                  onClick={() => setFilterType("assigned")}
                >
                  Поставленные мной
                </TabsTrigger>
                <TabsTrigger
                  value="all"
                  className={taskListTheme.tabTrigger}
                  onClick={() => setFilterType("all")}
                >
                  Все
                </TabsTrigger>
                <TabsTrigger
                  value="blocks"
                  className={taskListTheme.tabTriggerBlocks}
                  onClick={() => setFilterType("blocks")}
                >
                  Задачи по блокам
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="text-sm text-purple-300 whitespace-nowrap">По сроку:</span>
              <Select 
                value={deadlineFilter} 
                onValueChange={(value) => onDeadlineFilterChange?.(value as any)}
              >
                <SelectTrigger className={taskListTheme.selectTrigger + " w-56"}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={taskListTheme.selectContent}>
                  <SelectItem value="all">Все задачи</SelectItem>
                  <SelectItem value="overdue">Просрочено</SelectItem>
                  <SelectItem value="today">Сегодня</SelectItem>
                  <SelectItem value="tomorrow">Завтра</SelectItem>
                  <SelectItem value="thisWeek">На этой неделе</SelectItem>
                  <SelectItem value="nextWeek">На следующей неделе</SelectItem>
                  <SelectItem value="noDeadline">Без срока</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-purple-300 whitespace-nowrap">Сортировка:</span>
              <Select 
                value={sortBy} 
                onValueChange={(value) => setSortBy(value as any)}
              >
                <SelectTrigger className={taskListTheme.selectTrigger + " w-64"}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={taskListTheme.selectContent}>
                  <SelectItem value="urgency">Срочные вверху</SelectItem>
                  <SelectItem value="deadline-asc">Дедлайн (ближайшие сначала)</SelectItem>
                  <SelectItem value="deadline-desc">Дедлайн (отдалённые сначала)</SelectItem>
                  <SelectItem value="creator-asc">По автору (А → Я)</SelectItem>
                  <SelectItem value="creator-desc">По автору (Я → А)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </TaskListHeader>

        <div className={taskListTheme.tasksGrid}>
          {displayedRegularTasks.length === 0 ? (
            <p className={taskListTheme.emptyState + " text-2xl col-span-2 py-20"}>
              {deadlineFilter === "all" ? "Нет обычных задач" : "Нет задач по выбранному периоду"}
            </p>
          ) : (
            displayedRegularTasks.map((task) => (
              <TaskCard 
                key={task.id} 
                task={task} 
                onSelect={onTaskSelect} 
              />
            ))
          )}
        </div>
      </div>
    </div>
  )
}

const getTimestamp = (ts: any): number => {
  if (!ts) return 0
  if (ts instanceof Timestamp) return ts.toDate().getTime()
  if (typeof ts === "string") return new Date(ts).getTime()
  if (ts.seconds) return ts.seconds * 1000 + (ts.nanoseconds || 0) / 1000000
  return 0
}